import { z } from 'zod';
import { ErrorCode } from '../../shared/error-codes';
/**
 * Account Security Status Response Schema
 * GET /api/v2/profile/account-security
 *
 * `passwordChange`:
 * - 'enabled'     — the self-service surface is live; the password form
 *                   additionally requires `hasPassword`
 * - 'external'    — Tenant.sso.enforced: credentials live at the customer's
 *                   federated IdP; `ssoIdpName` carries its display name
 * - 'unavailable' — the user has no resolvable Zitadel account yet
 */ export const AccountSecurityStatusResponseSchema = z.object({
    passwordChange: z.enum([
        'enabled',
        'external',
        'unavailable'
    ]),
    // Whether the resolved Zitadel account carries a password credential.
    // Gates the in-app password form (passkey-only accounts have none to
    // change) and feeds the last-auth-method messaging: TOTP is a second
    // factor, not a primary method — only password and passkeys count there.
    // A sibling boolean rather than a fourth `passwordChange` mode: the enum
    // answers "which whole-tab surface renders", and a passkey-only account
    // keeps the full 'enabled' surface (TOTP and passkey sections included).
    // Always false when no Zitadel account is resolvable.
    hasPassword: z.boolean(),
    // Present only when passwordChange === 'external' (Tenant.sso.idpName)
    ssoIdpName: z.string().nullable(),
    totp: z.object({
        available: z.boolean(),
        enabled: z.boolean()
    }),
    passkeys: z.object({
        available: z.boolean(),
        items: z.array(z.object({
            id: z.string(),
            name: z.string()
        }))
    }),
    // Mirrors the SensitiveOperationGuard freshness check: true when the
    // session's authentication instant is too old for credential changes.
    reauthRequired: z.boolean(),
    // Whether this session can START an SSO step-up (requires a Zitadel-linked
    // identity) — NOT whether the sensitive operation itself is allowed.
    stepUpAvailable: z.boolean()
});
/**
 * TOTP Registration Schemas
 * POST /api/v2/profile/totp/register
 * `uri` is the otpauth:// URI rendered as a QR code client-side; `secret`
 * is the manual-entry fallback.
 */ export const RegisterTotpRequestSchema = z.object({});
export const RegisterTotpResponseSchema = z.object({
    uri: z.string(),
    secret: z.string()
});
/**
 * TOTP Verification Schemas
 * POST /api/v2/profile/totp/verify
 */ export const VerifyTotpRequestSchema = z.object({
    code: z.string().regex(/^\d{6}$/, ErrorCode.FIELD_REQUIRED)
});
export const VerifyTotpResponseSchema = z.object({
    success: z.boolean()
});
/**
 * TOTP Removal Schemas
 * DELETE /api/v2/profile/totp
 */ export const RemoveTotpRequestSchema = z.object({});
export const RemoveTotpResponseSchema = z.object({
    success: z.boolean()
});
/**
 * Passkey Registration Link Schemas
 * POST /api/v2/profile/passkeys/registration-link
 * WebAuthn credentials must be created on the auth origin (RP ID binding),
 * so registration happens on Zitadel's hosted Login V2 page —
 * `registrationUrl` deep-links there with a one-time code.
 */ export const CreatePasskeyRegistrationLinkRequestSchema = z.object({});
export const CreatePasskeyRegistrationLinkResponseSchema = z.object({
    registrationUrl: z.string()
});
/**
 * Passkey Removal Schemas
 * DELETE /api/v2/profile/passkeys/:passkeyId
 */ export const RemovePasskeyRequestSchema = z.object({});
export const RemovePasskeyResponseSchema = z.object({
    success: z.boolean()
});

//# sourceMappingURL=account-security.schema.js.map